import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Row, Col, Button, Spin, Image, Divider } from 'antd';
import {
  InstagramOutlined, FacebookOutlined, TwitterOutlined, YoutubeOutlined,
  AppleOutlined, SoundOutlined,
  BoldOutlined,
  SpotifyOutlined,
  MailOutlined,
  XOutlined,
  LinkedinOutlined,
  GlobalOutlined,
  EyeFilled
} from '@ant-design/icons';
import { fetchArtist } from '../../actions/ArtistsActions';
import './Artist.css';
import { FaDeezer } from 'react-icons/fa';
import { SiAudiomack } from 'react-icons/si';
import ArtistNavigation from './ArtistNavigation';
import FooterComponent from '../footer/Footer';
import { logo, sanecka, youtube } from '../assets/assets';
import { logPageVisit } from '../../actions/AnalyticsActions';
import ArtistList from './ArtistsList';

import youtubeSvg from '../../assets/socials/youtube.svg';
import youtubeMusicSvg from '../../assets/socials/youtubeMusic.svg';
import deezerSvg from '../../assets/socials/deezer.svg';
import audioMackSvg from '../../assets/socials/audiomack.svg';
import boomPlaySvg from '../../assets/socials/boomplay.svg';
import appleMusicSvg from '../../assets/socials/appleMusic.svg';
import amazonMusicSvg from '../../assets/socials/amazonMusic.svg';
import spotifySvg from '../../assets/socials/spotify.svg';
import { Helmet } from 'react-helmet';

const { Title, Text } = Typography;

const Artist = () => {
  const { artistId } = useParams();
  const dispatch = useDispatch();

  // Select artists array from Redux store
  const { artists, loading, error } = useSelector((state) => state.artists);
  const { translations, language } = useSelector((state) => state.language);

  const [seeAll, setSeeAll] = useState(false);

  useEffect(() => {
    if (artistId && (artistId !== '') && (artists?.length > 0)) {

      const artist = artists?.find((a) => a.id === artistId.toLowerCase()) || null;

      if (artist) {
        dispatch(logPageVisit(artist?.name || artistId));

      } else {
        dispatch(logPageVisit("Unknown Artist"));
      }

    }
  }, [dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSeeAll = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setSeeAll(true);

  }


  // Find the artist with the matching ID
  const artist = artists?.find((a) => a.id === artistId.toLowerCase()) || null;

  if (loading) return <div className="loading-container"><Spin size="large" /></div>;
  if (error) return <Text style={{ color: 'red', textAlign: 'center' }}>{error}</Text>;
  // if (!artist) return <Text style={{ textAlign: 'center' }}>Artist not found!</Text>;

  const { name, personalPicUrl, genre, bio, bioSw } = artist;
  const keywords = [...new Set([...bio.split(" "), ...bioSw.split(" ")])].join(", "); // Extract unique keywords

  return (
    <div >
      <Helmet>
        <title>{`${name} - Official Artist Profile | Sanecka Brand`}</title>
        <meta name="description" content={bio} />
        <meta name="keywords" content={`${name}, ${genre}, ${keywords}, Sanecka Brand, artist`} />
        <meta property="og:title" content={`${name} - Official Artist Profile`} />
        <meta property="og:description" content={bio} />
        <meta property="og:image" content={personalPicUrl} />
        <meta property="og:type" content="profile" />
      </Helmet>

      <ArtistNavigation setSeeAll={setSeeAll} artistName={artist?.name || translations.artist.unknownArtist} />
      <div style={{ minHeight: "100vh" }} className="artist-profile-container">
        {
          seeAll ?
            (
              <div className="artist-profile">
                <ArtistList artists={artists} artistId={artistId} setSeeAll={setSeeAll} />
              </div>
            )
            :
            (
              artist
                ?
                <div className="artist-profile">
                  {/* Profile Header */}
                  <div className="profile-header">
                    <img
                      src={artist.personalPicUrl || sanecka}
                      alt={artist.name}
                      className="profile-pic"
                    />
                    <Title level={2} className="profile-name">{artist.name}</Title>
                    <Text className="profile-genre">{artist.genre}</Text>
                    <Text className="profile-bio">
                      {(language === 'en' ? artist?.bio : artist?.bioSw)?.split('\n').map((line, index) => (
                        <span key={index}>
                          {line}
                          <br />
                        </span>
                      ))}
                    </Text>

                  </div>

                  {/* Streaming Platform Links */}
                  <div className="social-media">
                    <Title level={4} className="social-title">{translations.artist.streamEnjoy}</Title>
                    <Row gutter={[16, 16]} justify="center">

                      {artist.youtube && (
                        <Col>
                          <a href={artist.youtube} target="_blank" rel="noopener noreferrer">
                            <img src={youtubeSvg} alt={artist.youtube} width={100} height={50} className="stream-button" />
                          </a>
                        </Col>
                      )}
                      {artist.youtubeMusic && (
                        <Col>
                          <a href={artist.youtubeMusic} target="_blank" rel="noopener noreferrer">
                            <img src={youtubeMusicSvg} alt={artist.youtubeMusic} width={100} height={50} className="stream-button" />
                          </a>
                        </Col>
                      )}
                      {artist.deezer && (
                        <Col>
                          <a href={artist.deezer} target="_blank" rel="noopener noreferrer">
                            <img src={deezerSvg} alt={artist.deezer} width={100} height={50} className="stream-button" />
                          </a>
                        </Col>
                      )}
                      {artist.appleMusic && (
                        <Col>
                          <a href={artist.appleMusic} target="_blank" rel="noopener noreferrer">
                            <img src={appleMusicSvg} alt={artist.appleMusic} width={100} height={50} className="stream-button" />
                          </a>
                        </Col>
                      )}
                      {artist.amazonMusic && (
                        <Col>
                          <a href={artist.amazonMusic} target="_blank" rel="noopener noreferrer">
                            <img src={amazonMusicSvg} alt={artist.amazonMusic} width={100} height={50} className="stream-button" />
                          </a>
                        </Col>
                      )}
                      {artist.audioMack && (
                        <Col>
                          <a href={artist.audioMack} target="_blank" rel="noopener noreferrer">
                            <img src={audioMackSvg} alt={artist.audioMack} width={100} height={50} className="stream-button" />
                          </a>
                        </Col>
                      )}
                      {artist.boomPlay && (
                        <Col>
                          <a href={artist.boomPlay} target="_blank" rel="noopener noreferrer">
                            <img src={boomPlaySvg} alt={artist.boomPlay} width={100} height={50} className="stream-button" />
                          </a>
                        </Col>
                      )}
                      {artist.spotify && (
                        <Col>
                          <a href={artist.spotify} target="_blank" rel="noopener noreferrer">
                            <img src={spotifySvg} alt={artist.spotify} width={100} height={50} className="stream-button" />
                          </a>
                        </Col>
                      )}
                    </Row>
                  </div>


                  {/* Social Media Links */}
                  <div className="social-media">
                    <Title level={4} className="social-title">{translations.artist.contact} {artist.name}</Title>
                    <Row gutter={[16, 16]} justify="center">
                      {artist.email && (
                        <Col>
                          <Button type="link" href={`mailto:${artist.email}`} icon={<MailOutlined />} className="social-button">
                            {translations.artist.email}
                          </Button>
                        </Col>
                      )}
                      {artist.instagram && (
                        <Col>
                          <Button type="link" href={artist.instagram} target="_blank" rel="noopener noreferrer" icon={<InstagramOutlined />} className="social-button">
                            Instagram
                          </Button>
                        </Col>
                      )}
                      {artist.facebook && (
                        <Col>
                          <Button type="link" href={artist.facebook} target="_blank" rel="noopener noreferrer" icon={<FacebookOutlined />} className="social-button">
                            Facebook
                          </Button>
                        </Col>
                      )}
                      {artist.twitter && (
                        <Col>
                          <Button type="link" href={artist.twitter} target="_blank" rel="noopener noreferrer" icon={<XOutlined />} className="social-button">
                            Twitter
                          </Button>
                        </Col>
                      )}

                    </Row>
                  </div>

                  {/* Back Button */}
                  {/* <div className="back-button">
        <Button type="primary" href="/artists" className="back-btn">
          Back to Artists
        </Button>
      </div> */}
                </div>
                :
                <div className="artist-profile">
                  <Text level={2} style={{ textAlign: 'center', fontWeight: '500' }}>{translations.artist.notFound}</Text>
                </div>
            )
        }


        <div className="sanecka-profile" style={{ minHeight: '100vh' }}>
          <div className="sanecka-profile-main">
            <Image
              src={sanecka}
              alt={"sanecka brand"}
              preview={false}
            />
            {/* <Title level={2} className="profile-name">Sanecka Brand</Title> */}
            {/* <Text className="profile-genre">We Free You From Stress</Text> */}


            <div className="sanecka-profile-website">
              <Title level={4} className="social-title">{translations.artist.officialWebsite}</Title>
              <Row gutter={[16, 16]} justify="center">

                {/* {artist.email && ( */}
                <Col>
                  <Button type="link" icon={<GlobalOutlined />} className="social-button">
                    <Link to='/'>saneckabrand.com</Link>
                  </Button>
                </Col>
                {/* )} */}


              </Row>
            </div>
            <div className="sanecka-profile-contact">
              <Title level={4} className="social-title">{translations.artist.contactUs}</Title>
              <Row gutter={[16, 16]} justify="center">

                {/* {artist.email && ( */}
                <Col>
                  <Button type="link" href={`mailto:info@saneckabrand.com`} icon={<MailOutlined />} className="social-button">
                    {translations.artist.email}
                  </Button>
                </Col>
                {/* )} */}
                {/* {artist.instagram && ( */}
                <Col>
                  <Button type="link" href='https://linkedin.com/company/saneckabrand' target="_blank" rel="noopener noreferrer" icon={<LinkedinOutlined />} className="social-button">
                    LinkedIn
                  </Button>
                </Col>
                <Col>
                  <Button type="link" href='https://instagram.com/saneckabrand' target="_blank" rel="noopener noreferrer" icon={<InstagramOutlined />} className="social-button">
                    Instagram
                  </Button>
                </Col>
                {/* )} */}
                {/* {artist.facebook && ( */}
                <Col>
                  <Button type="link" href='https://facebook.com/saneckabrand' target="_blank" rel="noopener noreferrer" icon={<FacebookOutlined />} className="social-button">
                    Facebook
                  </Button>
                </Col>
                {/* )} */}
                {/* {artist.twitter && ( */}
                <Col>
                  <Button type="link" href='https://x.com/saneckabrand' target="_blank" rel="noopener noreferrer" icon={<XOutlined />} className="social-button">
                    Twitter
                  </Button>
                </Col>
                {/* )} */}


              </Row>
            </div>
          </div>


          <div style={{ alignItems: 'end' }}>
            <Divider style={{ marginTop: 5, marginBottom: 5 }} />
            <Button onClick={handleSeeAll} icon={<EyeFilled />} type="link" className='sanecka-profile-see-all'>
              {translations.artist.viewAll}
            </Button>
            <Divider style={{ marginTop: 5, marginBottom: 5 }} />
          </div>
          <div className='sanecka-profile-tanzator'>

            {/* Powered by Tanzator */}
            <Col xs={24} sm={24} md={24} style={{ textAlign: 'center', display: 'inline', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ fontSize: '14px', verticalAlign: 'top' }}>{translations.artist.powered}</Text>
              {/* <div style={{ display: 'flex', alignItems: 'center', }}> */}
              <a href="https://tanzator.com" target="_blank" rel="noopener noreferrer" >
                <img
                  src={logo}
                  alt="Tanzator Logo"
                  style={{ height: '30px', marginRight: '10px', marginLeft: '10px' }}
                  className='hover-tanzator'
                />
                <Text style={{ fontSize: '14px', verticalAlign: 'top' }} className='hover-tanzator'>Tanzator</Text>
              </a>
              {/* </div> */}
            </Col>
          </div>
        </div>
      </div>
      {/* <FooterComponent /> */}
    </div>
  );
};

export default Artist;
